import React, { useState, useEffect } from "react";
import { PlusIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import db from "../firebase-config.ts";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useUser } from './UserProvider.tsx'; 
import { useNavigate } from 'react-router-dom'; 

interface Student {
  nama_penuh: string;
  no_matrik: number;
  no_telefon: string;
  alamat_tempat_tinggal: string;
  daerah: string;
  dijangka_tamat_pengajian: string;
  jenis_bantuan: string;
  jurusan: string;
  kampus: string;
  kuliah: string;
  mahallah: string;
  penerima_bantuan: string;
  purata_pendapatan: string;
  sesi_kemasukan_ke_uia: string;
  tahun_pengajian: string;
}

const PAGE_SIZE = 5;

const UserManagement: React.FC = () => {
  const [allUsers, setAllUsers] = useState<Student[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<Student[]>([]);
  const [currentPageUsers, setCurrentPageUsers] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { isAuthenticated } = useUser(); // Access user authentication state
  const navigate = useNavigate(); // Initialize navigation


  useEffect(() => {
      if (!isAuthenticated) {
        navigate('/login'); // Redirect to login page
      }
    }, [isAuthenticated, navigate]);
  
  const fetchAllStudents = async () => {
    setIsLoading(true);
    try {
      const studentsCollection = collection(db, "students");
      const studentsQuery = query(studentsCollection, orderBy("nama_penuh"));
      const snapshot = await getDocs(studentsQuery);

      if (!snapshot.empty) {
        const studentsData = snapshot.docs.map((doc) => {
          const data = doc.data() as Student;
          return { id: doc.id, ...data };
        });

        setAllUsers(studentsData);
        setFilteredUsers(studentsData);
        paginateUsers(studentsData, 1); // Load the first page
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
    setIsLoading(false);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();
    const results = allUsers.filter((user) =>
      user.nama_penuh.toLowerCase().includes(lowerQuery)
    );
    setFilteredUsers(results);
    paginateUsers(results, 1); // Reset to the first page after search
  };

  const paginateUsers = (users: Student[], page: number) => {
    const startIndex = (page - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    setCurrentPageUsers(users.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage * PAGE_SIZE < filteredUsers.length) {
      paginateUsers(filteredUsers, currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      paginateUsers(filteredUsers, currentPage - 1);
    }
  };

  useEffect(() => {
    fetchAllStudents();
  }, []);

  return isAuthenticated ? (
    <div className="min-h-screen  text-gray-100">
      {/* Header */}
      <header className="p-6 shadow-md  text-white">
        <div>
          <h1 className="text-4xl font-extrabold">Student Management</h1>
          <p className="mt-2 text-lg">Manage student data with global search and pagination.</p>
        </div>
      </header>

      <main className="p-6">
        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full p-2 rounded-lg bg-gray-700 text-white"
          />
        </div>

        {/* Student Table */}
        <div className="bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-700">
                <tr className="text-left text-gray-300 text-sm uppercase tracking-wide">
                  <th className="px-4 py-3">Photo</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Matric No</th>
                  <th className="px-4 py-3">Major</th>
                  <th className="px-4 py-3">Campus</th>
                  <th className="px-4 py-3">Aid</th>
                  <th className="px-4 py-3">Income</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Year</th>
                  
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-6 text-gray-400">
                      Loading student data...
                    </td>
                  </tr>
                ) : currentPageUsers.length > 0 ? (
                  currentPageUsers.map((student, index) => (
                    <tr
                      key={student.no_matrik}
                      className={`${
                        index % 2 === 0 ? "bg-gray-900" : "bg-gray-800"
                      } hover:bg-gray-700 transition`}
                    >
                      <td className="px-4 py-3">
                        <img
                          src={`https://smartcard.iium.edu.my/packages/card/printing/camera/uploads/original/${student.no_matrik}.jpeg`}
                          alt={student.nama_penuh}
                          className="w-12 h-12 rounded-full object-cover shadow-md"
                        />
                      </td>
                      <td className="px-4 py-3">{student.nama_penuh}</td>
                      <td className="px-4 py-3">{student.no_matrik}</td>
                      <td className="px-4 py-3">{student.jurusan}</td>
                      <td className="px-4 py-3">{student.kampus}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-3 py-1 rounded-full text-sm ${
                            student.penerima_bantuan === "YA"
                              ? "bg-green-600 text-green-100"
                              : "bg-red-600 text-red-100"
                          }`}
                        >
                          {student.penerima_bantuan}
                        </span>
                      </td>
                      <td className="px-4 py-3">{student.purata_pendapatan}</td>
                      <td className="px-4 py-3">{student.no_telefon}</td>
                      <td className="px-4 py-3">{student.alamat_tempat_tinggal}</td>
                      <td className="px-4 py-3">{student.tahun_pengajian}</td>
                      
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center py-6 text-gray-400">
                      No students found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex justify-between">
          <button
            className="bg-gray-700 hover:bg-gray-600 text-white py-2 px-6 rounded-lg shadow-md disabled:opacity-50"
            disabled={currentPage === 1}
            onClick={handlePreviousPage}
          >
            Previous
          </button>
          <button
            className="bg-gray-700 hover:bg-gray-600 text-white py-2 px-6 rounded-lg shadow-md disabled:opacity-50"
            disabled={currentPage * PAGE_SIZE >= filteredUsers.length}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      </main>
    </div>
 ) : null; 
};

export default UserManagement;
